import React, { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import type { ButtonVariant } from '../types'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode | React.ComponentType<any>
  iconPosition?: 'start' | 'end'
  isLoading?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-[#146971] text-white shadow-[0_14px_32px_-20px_rgba(20,105,113,0.9)] hover:bg-[#0f565d]',
  secondary:
    'border border-[#146971] bg-transparent text-[#146971] hover:bg-[#f2faf9]',
  danger:
    'bg-danger text-white shadow-[0_20px_28px_-22px_rgba(220,38,38,1)] hover:bg-[#b91c1c]',
  fullWidth:
    'w-full bg-danger text-white shadow-[0_20px_28px_-22px_rgba(220,38,38,1)] hover:bg-[#b91c1c]',
  outline:
    'border border-[#E2E8F0] bg-white text-[#475569] hover:bg-slate-50',
}

const sizeClasses = {
  sm: 'h-8 px-4 py-1.5 text-btn-small rounded-md',
  md: 'h-10 px-4 py-2.5 text-btn-medium rounded-md',
  lg: 'h-11 px-4 py-3 text-btn-large rounded-md',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      icon,
      iconPosition = 'start',
      className = '',
      children,
      type = 'button',
      isLoading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_26px_-20px_rgba(15,23,42,0.65)] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#146971]/40 disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-none disabled:bg-neutral-300 disabled:text-white disabled:shadow-none disabled:scale-100 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        ) : icon && iconPosition === 'start' ? (
          <span className="inline-flex shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
            {typeof icon === 'function' || (typeof icon === 'object' && icon !== null && 'render' in (icon as any))
              ? React.createElement(icon as any, { className: 'h-4 w-4' })
              : icon}
          </span>
        ) : null}
        {children}
        {!isLoading && icon && iconPosition === 'end' ? (
          <span className="inline-flex shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
            {typeof icon === 'function' || (typeof icon === 'object' && icon !== null && 'render' in (icon as any))
              ? React.createElement(icon as any, { className: 'h-4 w-4' })
              : icon}
          </span>
        ) : null}
      </button>
    )
  }
)

Button.displayName = 'Button'

